import React, { Fragment } from 'react'
import actions from 'redux/actions'
import { useConnect } from 'sb-redux-hook'
import { useCallback, useEffect, useMedia } from 'sb-hooks'
import { constants, links, mixpanel, formatPrice } from 'common/helpers'
import { getActiveFlashSaleDataSelector } from 'common/helpers/getters'
import { FormattedHTMLMessage } from 'sb-react-intl'

import { Box, Overlay } from 'components/_new/layout'
import ModalContainer from 'components/_new/modal/ModalContainer/ModalContainer'
import Modal from 'components/_new/modal/Modal/Modal'
import Text from 'components/_new/typography/Text/Text'
import { Image } from 'components/_new/ui'
import { Button, ButtonsInRow } from 'components/_new/controls'

import Countdown from 'pages/bundle/FlashSaleBundlePage/BundleInfo/Countdown/Countdown'

import s from './FlashSaleModal.scss'
import messages from './messages'


const FlashSaleModal = () => {
  const { isMobile } = useMedia()

  const { data, timeLeft, startDate, isTwoFlashSalesAvailable } = useConnect({
    data: getActiveFlashSaleDataSelector({ activeGenderOnly: true }),
    timeLeft: 'view.flashSaleBundlePage.timeLeft',
    startDate: 'view.flashSaleBundlePage.startDate',
    isTwoFlashSalesAvailable: 'view.flashSaleBundlePage.isTwoFlashSalesAvailable',
  })

  const { name: title, gender, media, price: priceCents, discount: discountCents } = data

  const image = media ? media[0] : null

  const discount = formatPrice(discountCents, true)
  // remove cents from formatted price only if it's "00"
  const price = formatPrice(priceCents, true)
    .replace('.00', '')

  const oppositeGender = gender === 'male' ? 'female' : 'male'
  const oppositeGenderLink = `${links.flashSale}?gender=${oppositeGender}`
  const oppositeGenderTitle = messages.oppositeLink[oppositeGender]

  const close = useCallback(() => {
    const startDateTime = new Date(startDate).getTime()

    actions.models.modals.close(constants.modals.humble.flashSale)
    actions.models.ui.setClosedPopup(`${constants.modals.humble.flashSale}${startDateTime}`)
  }, [ startDate ])

  const onClick = useCallback(() => {
    mixpanel.track(constants.analyticsEvents.flashSale.popupClick)
    close()
  }, [ close ])

  useEffect(() => {
    mixpanel.track(constants.analyticsEvents.flashSale.popup)
  }, [])

  return (
    <Fragment>
      <ModalContainer>
        <Overlay onClick={close} />
        <Modal onClose={close}>
          <Box mb={24}>
            <Text message={messages.title} tag="span" size="32-40" semibold html />
            <Countdown timeLeft={timeLeft} brandColor />
            <Text message={messages.left} tag="span" size="32-40" semibold html />
          </Box>
          <Text
            className={s.text}
            message={{ ...messages.text, values: { title, price, discount } }}
            html
          />
          {
            image ? (
              <Box className={s.imageContainer} mt={24}>
                <Image className={s.imageSolo} src={image} w={isMobile ?  400 : 600} x2 />
              </Box>
            ) : (
              <div className={s.image}>
                <FormattedHTMLMessage className={s.discount} {...messages.discount} />
              </div>
            )
          }
          <Box mt={40}>
            <ButtonsInRow alignLeft>
              <Button
                className={s.button}
                title={messages.buttonTitle}
                size="md"
                color="brand"
                rightIcon="rightArrowSmall"
                fullWidthOnMobile
                to={links.flashSale}
                onClick={onClick}
              />
              {
                isTwoFlashSalesAvailable && (
                  <Button
                    className={s.button}
                    title={oppositeGenderTitle}
                    size="md"
                    color="brandTransparent"
                    fullWidthOnMobile
                    to={oppositeGenderLink}
                    onClick={onClick}
                  />
                )
              }
            </ButtonsInRow>
          </Box>
        </Modal>
      </ModalContainer>
    </Fragment>
  )
}


export default FlashSaleModal
